import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Box } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import Select from 'react-select';
import { CloudUpload, AttachFile } from '@material-ui/icons';
import { CircularProgress } from '@mui/material';
import React from 'react';
import ReduxLanguage from '../ReduxLanguage';
import translations from '../../translations/i18next';
import * as ModalsActions from '../../redux/modals/modals.actions';
import { GREEN_MAIN } from '../../style/StyleConsts';
import { MKSImportFileFormatOptions } from '../MKS/MKSRequestsTable';
import { connect, ConnectedProps } from 'react-redux';
import { ModalTypes } from './ModalTypes';

export interface ICsvUploadModalProps {
  open: boolean;
  onClose: () => void;
  onFileSelect: (file: File) => void;
  onFileFormatSelect: (fileFormat: string) => void;
  onPreview: () => void;
  selectedFile: File | null;
  isLoading: boolean;
  error: string;
}

export interface IFileFormatOption {
  label: string;
  value: string;
}
export interface ICsvUploadModalState {
  dragOver: boolean;
  fileFormatSelected: IFileFormatOption | null;
}

type IReduxProps = ConnectedProps<typeof connector>;

export type CsvUploadModalProps = ICsvUploadModalProps & IReduxProps;
export class CsvUploadModal extends React.Component<CsvUploadModalProps, ICsvUploadModalState> {
  private fileInputRef: React.RefObject<HTMLInputElement>;

  constructor(props: CsvUploadModalProps) {
    super(props);
    this.state = {
      dragOver: false,
      fileFormatSelected: null as IFileFormatOption | null,
    };
    this.fileInputRef = React.createRef();
  }

  public onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/csv') {
      this.props.onFileSelect(file);
    }
  };

  public onSelectFileFormat = (fileFormat: IFileFormatOption | null) => {
    this.setState({ fileFormatSelected: fileFormat });
    this.props.onFileFormatSelect(fileFormat?.value || MKSImportFileFormatOptions.LOCKBASE);
  };

  public onDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    this.setState({ dragOver: true });
  };

  public onDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    this.setState({ dragOver: false });
  };

  public onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    this.setState({ dragOver: false });
    
    const file = event.dataTransfer.files[0];
    if (file && (file.type === 'text/csv' || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel')) {
      this.props.onFileSelect(file);
    } else {
      const { dispatch } = this.props;
      dispatch<any>(ModalsActions.showModal(`ERROR_FILE_FORMAT_NOT_SUPPORTED`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.fileFormatNotSupported',
        }
      }));
    }
  };

  public onBrowseFiles = () => {
    this.fileInputRef.current?.click();
  };

  public render() {
    const { open, onClose, onPreview, selectedFile, isLoading, error } = this.props;
    const { dragOver, fileFormatSelected } = this.state;

    const fileFormatOptions: IFileFormatOption[] = Object.keys(MKSImportFileFormatOptions).map(key => ({
      label: translations.t(`mks.importFileFormat_${key}`),
      value: key,
    }));

    return (
      <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <ReduxLanguage languageKey="mks.uploadCsvTitle" />
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              {error}
            </Alert>
          )}
          <h3 style={{ fontWeight: 'bold' }}><ReduxLanguage languageKey="mks.selectFileFormat" /><span style={{ color: 'red' }}>*</span></h3>
          <Select
            isClearable
            value={fileFormatSelected}
            options={fileFormatOptions}
            placeholder={<><ReduxLanguage languageKey={'mks.importFileFormat'} /></>}
            formatOptionLabel={(o) => <>{o.label}</>}
            getOptionLabel={(o) => `${o.label}`}
            onChange={(e) => this.onSelectFileFormat(e as IFileFormatOption | null)}
            styles={{
              menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
              control: (styles) => ({ ...styles, minHeight: 55, minWidth: 250, marginLeft: 10, marginRight: 10,}),
            }}
          />
          <Box
            onDragOver={this.onDragOver}
            onDragLeave={this.onDragLeave}
            onDrop={this.onDrop}
            style={{
              border: `2px dashed ${dragOver ? GREEN_MAIN : '#ccc'}`,
              borderRadius: 8,
              padding: 32,
              textAlign: 'center',
              backgroundColor: dragOver ? '#f0f8f0' : '#fafafa',
              cursor: 'pointer',
              marginBottom: 16,
              marginTop: 16,
              marginLeft: 10,
              marginRight: 10,
            }}
            onClick={this.onBrowseFiles}
          >
            <CloudUpload style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
            <Typography variant="h6" gutterBottom>
              <ReduxLanguage languageKey="mks.dragDropCsv" />
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <ReduxLanguage languageKey="mks.orClickToSelect" />
            </Typography>
          </Box>

          {selectedFile && (
            <Box style={{ display: 'flex', alignItems: 'center', padding: 16, backgroundColor: '#e8f5e8', borderRadius: 8 }}>
              <AttachFile style={{ marginRight: 8, color: GREEN_MAIN }} />
              <Typography variant="body1">
                {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
              </Typography>
            </Box>
          )}

          <input
            ref={this.fileInputRef}
            type="file"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            style={{ display: 'none' }}
            onChange={this.onFileInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            <ReduxLanguage languageKey="forms.cancel" />
          </Button>
          <Button
            onClick={onPreview}
            variant="contained"
            color="primary"
            disabled={!selectedFile || isLoading || !fileFormatSelected}
            startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
          >
            <ReduxLanguage languageKey="mks.previewCsv" />
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}


const connector = connect();
export default connector(CsvUploadModal);
